import { type ModalRegistration } from '@/shared/components/Modals/modal-registry/modal-registry.types';

import { BillingEventPayloadModal } from '../components/BillingEventPayloadModal/BillingEventPayloadModal';
import { EntitlementGrantModal } from '../components/EntitlementGrantModal/EntitlementGrantModal';
import { EntitlementRevokeModal } from '../components/EntitlementRevokeModal/EntitlementRevokeModal';
import { OrderDecisionModal } from '../components/OrderDecisionModal/OrderDecisionModal';

/**
 * The module's dialog TYPES.
 *
 * Global strings, so they carry the module name: the registry throws on a
 * collision rather than letting whichever registered last silently win.
 */
export const PAYMENT_MODALS = {
  DECISION: 'payments.decision',
  EVENT_PAYLOAD: 'payments.eventPayload',
  ENTITLEMENT_GRANT: 'payments.entitlementGrant',
  ENTITLEMENT_REVOKE: 'payments.entitlementRevoke',
} as const;

/**
 * The registrations `app.config.tsx` installs.
 *
 * **One dialog for two acts.** Rejecting and refunding both need a reason and
 * both are irreversible; which one is being taken travels in the payload
 * alongside the order's id, and the dialog re-reads the order itself —
 * `ModalData` is serializable-only, and a row object smuggled through the store
 * would be a snapshot of a status a colleague may have changed since.
 *
 * Submitting, confirming and discarding are **not** here: none of them takes a
 * field, so each is a confirmation with the sentence that says what it does
 * (`useOrderDecisions`). The order editor is not here either — it is a page.
 *
 * **The two entitlement writes are dialogs for the same reason the decisions
 * are**: a comp needs a note and a revoke needs a reason, both required by
 * contract, and a required field belongs to a dialog rather than to an optional
 * key on a confirmation (P20b). Each takes ids and re-reads the member itself,
 * because whether a grant *replaces* a live comp is decided from those rows and
 * a smuggled snapshot could be one a colleague has already changed.
 */
export const paymentsModals: readonly ModalRegistration[] = [
  { modalTypes: [PAYMENT_MODALS.DECISION], component: OrderDecisionModal },
  // The payload dialog takes the event's **id** and fetches it itself, for the
  // decision modal's reason and one more of its own: the read is audited
  // server-side, so it must happen when somebody actually opens the dialog and
  // never when a list row was merely drawn.
  { modalTypes: [PAYMENT_MODALS.EVENT_PAYLOAD], component: BillingEventPayloadModal },
  { modalTypes: [PAYMENT_MODALS.ENTITLEMENT_GRANT], component: EntitlementGrantModal },
  { modalTypes: [PAYMENT_MODALS.ENTITLEMENT_REVOKE], component: EntitlementRevokeModal },
];
